import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { Children, forwardRef } from 'react'

import { DIRECTION } from '../swiper/Swiper'

import { PreviousArrow, NextArrow } from './arrows/index'
import styles from './custom-swiper.module.scss'
import { getArrowClasses } from './helper'
import { useCustomSwiper } from './hooks/useCustomSwiper'
import { useTrackEvents } from './hooks/useTrackEvents'
import { SwiperProps } from './types/custom-swiper.types'

const SuperSwiper = forwardRef<HTMLDivElement, SwiperProps>(function SuperSwiperEl(props) {
  const {
    containerRef,
    activeIndex,
    isBeginning,
    isEnd,
    scrollToNext,
    scrollToPrev,
    canNavigate,
    totalSlides,
    handleScroll,
  } = useCustomSwiper(props)
  const {
    showDots,
    arrowColor,
    spaceBetween,
    dotsPosition,
    children,
    wrapperClass,
    className,
    slideClassName,
    containerClassName,
    isVertical,
    prevIconClass,
    nextIconClass,
    arrowOnClick,
    scrollSnap,
    paginationClassName,
    paginationDotClassName,
    paginationDotActiveClassName,
  }: SwiperProps = props

  const { trackNext, trackPrev } = useTrackEvents(props)

  const { t } = useTranslation('common')
  return (
    <>
      <div className={getArrowClasses({ ...props, canNavigate, totalSlides })}>
        <PreviousArrow
          {...props}
          id="prev-swiper-arrow"
          className={classnames(styles.prevArrow, styles.navIcon, styles.navIcon__prev, prevIconClass)}
          arrowColor={arrowColor}
          disabled={isBeginning}
          ariaLabel={props.ariaLabel ?? t('previous')}
          onClick={() => {
            scrollToPrev()
            arrowOnClick &&
              arrowOnClick({
                direction: DIRECTION.PREV,
              })
            trackPrev()
          }}
        />
        <NextArrow
          {...props}
          id="next-swiper-arrow"
          className={classnames(styles.nextArrow, styles.navIcon, nextIconClass)}
          arrowColor={arrowColor}
          disabled={isEnd}
          ariaLabel={props.ariaLabel ?? t('next')}
          onClick={() => {
            scrollToNext()
            arrowOnClick &&
              arrowOnClick({
                direction: DIRECTION.NEXT,
              })
            trackNext()
          }}
        />
      </div>
      <div
        className={classnames(styles.swiperContainer, containerClassName)}
        ref={containerRef}
        tabIndex={0}
        style={{
          ...(scrollSnap && { scrollSnapType: isVertical ? 'y mandatory' : 'x mandatory', scrollBehavior: 'smooth' }),
        }}
      >
        <div
          className={classnames(wrapperClass, className, styles.swiperWrapper)}
          role="group"
          style={{
            flexDirection: isVertical ? 'column' : 'row',
            // @i18n-ignore
            width: 'max-content',
          }}
          onScroll={handleScroll}
        >
          {Children.map(children, (child, index) => (
            <div
              key={index}
              className={classnames(slideClassName, styles.swiperSlide, {
                [styles.active ?? '']: index === activeIndex,
              })}
              style={{
                // @i18n-ignore
                scrollSnapAlign: 'start',
                ...(scrollSnap && { scrollSnapStop: 'always', flexShrink: 0 }),
                marginRight: spaceBetween,
                marginBottom: isVertical ? '5px' : 'none',
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {showDots && (
        <div
          className={classnames(
            styles.pagination,
            { [styles[`dot_position__${dotsPosition}`] ?? '']: dotsPosition },
            paginationClassName,
          )}
        >
          {Children.map(children, (_, index) => (
            <span
              key={index}
              className={classnames(
                styles.dot,
                paginationDotClassName,
                { [styles.active ?? '']: index === activeIndex },
                { [paginationDotActiveClassName ?? '']: index === activeIndex },
              )}
            />
          ))}
        </div>
      )}
    </>
  )
})

export default SuperSwiper
